import React from 'react';
import { formatMoney } from '../../../utils/currency';
import type { CurrencyCode } from '@bbpos/types';
import { Badge } from '../../BasicComponents/Badge';
import { Card } from '../../CompositeComponents/Card';
import type { CreditAccountCardProps } from './types';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmt(paise: number, currency: CurrencyCode) {
  return formatMoney(paise, currency);
}

// ─── Credit usage gauge ───────────────────────────────────────────────────────

function CreditGauge({
  outstanding,
  creditLimit,
  overdue,
  currency,
}: {
  outstanding: number;
  creditLimit: number;
  overdue: boolean;
  currency: CurrencyCode;
}) {
  const pct = creditLimit > 0 ? Math.min(100, Math.round((outstanding / creditLimit) * 100)) : 0;
  const isExceeded = outstanding > creditLimit;

  const barColor = isExceeded || overdue
    ? 'bg-danger-500'
    : pct >= 80
      ? 'bg-warning-500'
      : 'bg-brand-500';

  return (
    <div>
      <div
        role="meter"
        aria-valuenow={outstanding}
        aria-valuemin={0}
        aria-valuemax={creditLimit}
        aria-label={`Credit used: ${fmt(outstanding, currency)} of ${fmt(creditLimit, currency)}`}
        className="h-2.5 w-full rounded-full bg-neutral-200 overflow-hidden"
      >
        <div
          className={['h-full rounded-full transition-all', barColor].join(' ')}
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="flex justify-between text-xs text-neutral-500 mt-1 tabular-nums">
        <span>{fmt(outstanding, currency)} used</span>
        <span>{fmt(creditLimit, currency)} limit</span>
      </div>
    </div>
  );
}

// ─── CreditAccountCard ────────────────────────────────────────────────────────

export function CreditAccountCard({
  customer,
  creditLimit,
  outstanding,
  overdue = false,
  aging,
  onClick,
  loading = false,
  currency = 'INR',
  className,
}: CreditAccountCardProps) {
  const isExceeded = outstanding > creditLimit;
  const available = Math.max(0, creditLimit - outstanding);

  if (loading) {
    return (
      <div className={['space-y-2', className ?? ''].filter(Boolean).join(' ')} aria-busy="true" aria-label="Loading credit account">
        {[80, 60, 100, 40].map((w, i) => (
          <span
            key={i}
            className="block h-4 rounded bg-neutral-200 animate-pulse-soft"
            style={{ width: `${w}%` }}
          />
        ))}
      </div>
    );
  }

  return (
    <Card
      variant={overdue ? 'status' : 'outlined'}
      statusColor={overdue ? 'danger-500' : undefined}
      padding="md"
      onClick={onClick}
      className={[
        overdue ? 'animate-pulse-soft border-danger-400' : '',
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
      aria-label={`Credit account for ${customer.name}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div>
          <p className="font-semibold text-neutral-900 text-sm">{customer.name}</p>
          <p className="text-xs text-neutral-500">{customer.phone}</p>
        </div>
        <div className="flex gap-1.5">
          {overdue && <Badge variant="overdue" size="sm">Overdue</Badge>}
          {isExceeded && <Badge variant="rejected" size="sm">Limit exceeded</Badge>}
        </div>
      </div>

      {/* Limit exceeded banner */}
      {isExceeded && (
        <div
          role="alert"
          className="flex items-center gap-1.5 bg-danger-50 border border-danger-200 rounded-md px-3 py-1.5 mb-3 text-xs text-danger-700"
        >
          Credit limit exceeded by {fmt(outstanding - creditLimit, currency)}
        </div>
      )}

      {/* Gauge */}
      <CreditGauge
        outstanding={outstanding}
        creditLimit={creditLimit}
        overdue={overdue}
        currency={currency}
      />

      {/* Available */}
      <p className="text-xs text-neutral-500 mt-2 tabular-nums">
        Available: <span className="font-semibold text-neutral-800">{fmt(available, currency)}</span>
      </p>

      {/* Aging buckets */}
      <div className="mt-3 pt-3 border-t border-neutral-200">
        <p className="text-xs font-medium text-neutral-600 mb-2">Aging</p>
        <ul className="grid grid-cols-2 gap-1.5 text-xs tabular-nums">
          {[
            { label: 'Current', value: aging.current, color: 'text-success-600' },
            { label: '1–30 days', value: aging.days30, color: 'text-warning-600' },
            { label: '31–60 days', value: aging.days60, color: 'text-orange-600' },
            { label: '60+ days', value: aging.days90plus, color: 'text-danger-600' },
          ].map(({ label, value, color }) => (
            <li key={label} className="flex justify-between rounded bg-neutral-50 px-2 py-1">
              <span className="text-neutral-500">{label}</span>
              <span className={['font-semibold', color].join(' ')}>{fmt(value, currency)}</span>
            </li>
          ))}
        </ul>
      </div>

      {onClick && (
        <p className="text-xs text-brand-600 mt-3 text-right font-medium">
          View ledger →
        </p>
      )}
    </Card>
  );
}
